/**
 * Support page "Getting started": replay the product tour, plus a plain-English
 * overview of how the whole app fits together.
 */
import React, { useState } from 'react';
import { useProductTour } from '../../contexts/ProductTourContext';

interface Section {
  id: string;
  title: string;
  points: string[];
}

const SECTIONS: Section[] = [
  {
    id: 'customers',
    title: '1. Customers and folders',
    points: [
      'Everything starts with a customer. Open one from Home, or add a new one with Add Customer.',
      "Inside a customer, folders hold one piece of work each (a renewal, a new quote, a claim). Folders can sit inside other folders.",
      'Customers and prospects work the same way; convert a prospect to a customer when you win the business.',
    ],
  },
  {
    id: 'documents',
    title: '2. Adding documents',
    points: [
      "Drop files onto a folder's Documents tab, or drop them into Arie and let her work out which customer and folder they belong to.",
      'Give each document a short, clear name. The names become the column headings in your reports.',
      'PDFs, Word, Excel and emails (.eml/.msg) are all supported. Attachments inside emails are pulled out for you.',
    ],
  },
  {
    id: 'tools',
    title: '3. Running an analysis',
    points: [
      'Open a folder, go to Run Workflow and pick the tool for the line of business: Cyber, D&O, Home Insurance, Commercial Lines, Loss Runs and so on.',
      'Choose the documents, add a sentence about what you need, and RiskNinja picks the right analysis: a benchmark, renewal comparison, quote comparison and more.',
      'The earlier tools (year-over-year, coverage analysis, document comparison…) are under Legacy.',
      'To choose an exact analysis yourself, switch Workflow view to Dev in your account menu. Every individual tool is listed there.',
      'Reports run in the background. The bell lets you know when they are ready.',
    ],
  },
  {
    id: 'reports',
    title: '4. Reports and exports',
    points: [
      "Finished reports are on the folder's Reports tab. Click any citation to see the exact spot in the source document.",
      'Chat with a report to ask follow-up questions, or regenerate it with more context.',
      'Export to a branded PDF, Excel or a proposal document to send to your client.',
    ],
  },
  {
    id: 'arie',
    title: '5. Arie, your assistant',
    points: [
      'Click the ninja in the corner to talk to Arie. Ask coverage questions, find documents, file new ones, start analyses, or have her draft a summary or email.',
      'Anything that changes your data appears as a card you confirm first.',
    ],
  },
  {
    id: 'email',
    title: '6. Emailing Arie',
    points: [
      "If your agency has the agent switched on, send or forward an email to your company's Arie mailbox (shown under Agent activity → Settings) from the address on your account.",
      'Say what you want above the forwarded message. Arie files the attachments, does the work and emails you a summary with the reports and filled templates attached.',
      'Reply to her email to continue, e.g. to send a document she said was missing. Arie never emails clients; replies for them are saved as drafts for you.',
      'Steps that need your OK wait under Arie → Approvals.',
    ],
  },
  {
    id: 'library',
    title: '7. Library and settings',
    points: [
      'Library holds your Templates, the agency Knowledge Base (documents Arie can draw on) and Insights.',
      'Your account menu (top right) has Settings, password and the forwarding addresses you use to email Arie.',
      "Admins manage users, branding and the agency's Arie settings under Admin.",
    ],
  },
];

const AppOverview: React.FC = () => {
  const { startTour } = useProductTour();
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="mx-4 mb-6 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-secondary dark:text-dark-text">Getting started</h2>
          <p className="text-sm text-accent dark:text-dark-muted mt-1">New to RiskNinja, or want a refresher on what's changed?</p>
        </div>
        <button
          type="button"
          onClick={startTour}
          className="bg-[#1993e5] hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          Take the tour
        </button>
      </div>

      <h3 className="text-sm font-semibold text-secondary dark:text-dark-text mt-5 mb-2">How RiskNinja works</h3>
      <div className="divide-y divide-gray-100 dark:divide-dark-border border-y border-gray-100 dark:border-dark-border">
        {SECTIONS.map((section) => {
          const expanded = open === section.id;
          return (
            <div key={section.id}>
              <button
                type="button"
                onClick={() => setOpen(expanded ? null : section.id)}
                aria-expanded={expanded}
                className="w-full flex items-center justify-between py-2.5 text-left text-sm font-medium text-secondary dark:text-dark-text hover:text-primary"
              >
                {section.title}
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {expanded && (
                <ul className="pb-3 pl-5 list-disc space-y-1.5 text-sm text-gray-700 dark:text-gray-300">
                  {section.points.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AppOverview;
